import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import back_app from '../../lib/back_app';
import { useCatIds, cat_id2lbl } from '../../lib/cat';
import { country_ids, country_id2lbl } from '../../lib/country';
import platform from '../../lib/platform';
import { confetti_fire } from '../../lib/confetti';
import CreatorHeader from '../../components/creator/CreatorHeader';

let langs = [
  {id: 'en', lbl: 'English'}, {id: 'he', lbl: 'Hebrew'},
  {id: 'es', lbl: 'Spanish'}, {id: 'fr', lbl: 'French'},
  {id: 'de', lbl: 'German'}, {id: 'ar', lbl: 'Arabic'},
  {id: 'pt', lbl: 'Portuguese'}, {id: 'it', lbl: 'Italian'},
  {id: 'nl', lbl: 'Dutch'}, {id: 'sv', lbl: 'Swedish'},
  {id: 'no', lbl: 'Norwegian'}, {id: 'da', lbl: 'Danish'},
  {id: 'fi', lbl: 'Finnish'}, {id: 'pl', lbl: 'Polish'},
  {id: 'ja', lbl: 'Japanese'}, {id: 'ko', lbl: 'Korean'},
  {id: 'zh', lbl: 'Chinese'},
];

let field_cls = 'w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 '
  + 'rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent';

export default function CreatorOrg() {
  let navigate = useNavigate();
  let [search_params] = useSearchParams();
  let is_dbg = search_params.get('dbg') == '1';
  let { ids: cat_ids } = useCatIds();
  let [loading, loading_set] = useState(true);
  let [saving, saving_set] = useState(false);
  let [is_brand, is_brand_set] = useState(false);
  let [flag_loading, flag_loading_set] = useState(false);
  let [org_socials, org_socials_set] = useState<Record<string, any>>({});
  let [org_tier, org_tier_set] = useState<string>('');
  let [alert_msg, alert_msg_set] = useState<string>('');
  let [logo_modal_open, logo_modal_open_set] = useState(false);
  let [logo_url_input, logo_url_input_set] = useState('');
  let [logo_url_err, logo_url_err_set] = useState('');
  let [uploading_logo, uploading_logo_set] = useState(false);
  let [saving_logo_url, saving_logo_url_set] = useState(false);
  let [form, form_set] = useState({lbl: '', logo_url: '', bio: '',
    cats: {} as Record<string, 1>, country: '', language: 'en'});
  
  let [orig, orig_set] = useState(form);

  useEffect(() => {
    org_fetch();
  }, []);

  let org_fetch = async () => {
    try {
      let org = await back_app.org_get();
      if (org?.err)
        throw new Error(org.err);
      is_brand_set(!!org?.flag?.is_brand);
      let _form = {lbl: org?.lbl || '', logo_url: org?.logo_url || '',
        bio: org?.bio || '', cats: org?.cats || {},
        country: country_id2lbl(org?.country) ? org.country : '',
        language: org?.language || 'en'};
      form_set(_form);
      orig_set(_form);
      let social = org?.creator?.social || {};
      let platforms: Record<string, any> = {};
      for (let k in social)
      {
        if (k == 'tier')
          continue;
        if (social[k])
          platforms[k] = social[k];
      }
      org_socials_set(platforms);
      org_tier_set(social.tier || org?.creator?.tier || '');
    } catch (err) {
      console.error('org fetch failed', err);
      navigate('/login');
    } finally {
      loading_set(false);
    }
  };

  let brand_flag_toggle = async () => {
    flag_loading_set(true);
    try {
      let next = !is_brand;
      let res = await back_app.org_set('flag.is_brand', next);
      if (!res?.err)
        is_brand_set(next);
    } catch (err) {
      console.error('toggle brand flag failed', err);
    }
    flag_loading_set(false);
  };

  let logo_url_use = async () => {
    logo_url_err_set('');
    let url = logo_url_input.trim();
    if (!url)
      return logo_url_err_set('Enter a URL');
    if (!/^https?:\/\//u.test(url))
      return logo_url_err_set('URL must start with http:// or https://');
    saving_logo_url_set(true);
    try {
      let res = await back_app.org_logo_from_url(url);
      if (res?.err)
        return logo_url_err_set(`Failed: ${res.err}`);
      let s3_url = res.logo_url || res.url || '';
      if (!s3_url)
        return logo_url_err_set('No URL returned');
      form_set(prev => ({...prev, logo_url: s3_url}));
      logo_url_input_set('');
      logo_modal_open_set(false);
    } catch (err) {
      console.error('logo url upload failed', err);
      logo_url_err_set('Upload failed');
    } finally {
      saving_logo_url_set(false);
    }
  };

  let logo_file_chosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let file = e.target.files?.[0];
    if (!file)
      return;
    if (!file.type.startsWith('image/'))
      return alert_msg_set('Please select an image file (PNG, JPG)');
    if (file.size > 2 * 1024 * 1024)
      return alert_msg_set('Logo must be less than 2MB');
    uploading_logo_set(true);
    try {
      let body = new FormData();
      body.append('file', file);
      let res = await back_app.org_logo_upload(body);
      if (res.err)
        throw new Error(res.err);
      form_set(prev => ({...prev, logo_url: res.logo_url || res.url || ''}));
      logo_modal_open_set(false);
    } catch (err) {
      console.error('logo upload failed', err);
      alert_msg_set('Error uploading logo. Please try again.');
    } finally {
      uploading_logo_set(false);
    }
  };

  let save = async () => {
    let keys = ['lbl', 'bio', 'country', 'language'] as const;
    let sets = keys.map(k => [k, form[k].trim()])
      .filter(([k, v]) => v != orig[k as typeof keys[number]]);
    let cats_v = Object.keys(form.cats).sort().join(',');
    let is_cats_changed = cats_v != Object.keys(orig.cats).sort().join(',');
    if (!sets.length && !is_cats_changed)
      return;
    saving_set(true);
    try {
      for (let [k, v] of sets)
      {
        let res = await back_app.org_set(k, v);
        if (res?.err)
          return alert_msg_set(`Error saving ${k}: ${res.err}`);
      }
      if (is_cats_changed)
      {
        let res = await back_app.org_set('cats', cats_v);
        if (res?.err)
          return alert_msg_set(`Error saving categories: ${res.err}`);
      }
      orig_set({...orig, ...Object.fromEntries(sets), cats: form.cats});
      confetti_fire();
    } catch (err) {
      console.error('org save failed', err);
      alert_msg_set('Error saving profile');
    } finally {
      saving_set(false);
    }
  };

  if (loading)
  {
    return (
      <div className="min-h-screen bg-gray-50">
        <CreatorHeader />
        <div className="flex items-center justify-center h-96">
          <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  let logo_busy = uploading_logo || saving_logo_url;
  return (
    <>
    <div className="min-h-screen bg-gray-50">
      <CreatorHeader />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Channel Profile</h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            How brands see you. Used to match you with campaigns
          </p>
        </div>

        <div className="bg-white rounded-xl border shadow-sm">
          <div className="p-4 sm:p-6 border-b">
            <h3 className="text-base sm:text-lg font-bold text-gray-900">Basic Information</h3>
          </div>

          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Logo</label>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-gray-100
                  to-gray-200 flex items-center justify-center overflow-hidden border-4
                  border-white shadow-lg flex-shrink-0">
                  {form.logo_url ? (
                    <img src={form.logo_url} alt="Logo" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-brand flex items-center justify-center">
                      <span className="text-3xl text-white font-bold">
                        {(form.lbl || '?').charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <button
                    type="button"
                    onClick={() => { logo_url_err_set(''); logo_modal_open_set(true); }}
                    disabled={logo_busy}
                    className={`inline-block px-6 py-3 border-2 border-gray-300 rounded-lg
                      text-sm font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer
                      transition-all ${logo_busy ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {logo_busy ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent
                          rounded-full animate-spin"/>
                        {uploading_logo ? 'Uploading...' : 'Fetching...'}
                      </span>
                    ) : 'Change Logo'}
                  </button>
                  <p className="text-xs text-gray-500 mt-2">
                    PNG or JPG • Max 2MB • Recommended: Square 512×512px
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Display Name</label>
              <input type="text" value={form.lbl}
                onChange={e => form_set({...form, lbl: e.target.value})}
                className={field_cls} placeholder="How your name appears to brands" />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Bio</label>
              <textarea value={form.bio} rows={4} maxLength={1000}
                onChange={e => form_set({...form, bio: e.target.value})}
                className={field_cls} placeholder="Tell brands about your content..." />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Country
              </label>
              <select value={form.country}
                onChange={e => form_set({...form, country: e.target.value})}
                className={field_cls}>
                <option value="">Select your country</option>
                {country_ids.map(id => (
                  <option key={id} value={id}>{country_id2lbl(id)}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                This helps match you with relevant campaigns
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Primary Language</label>
              <select value={form.language}
                onChange={e => form_set({...form, language: e.target.value})}
                className={field_cls}>
                {langs.map(l => <option key={l.id} value={l.id}>{l.lbl}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border shadow-sm mt-4 sm:mt-6">
          <div className="p-4 sm:p-6 border-b">
            <h3 className="text-base sm:text-lg font-bold text-gray-900">
              Content Category
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Select the categories that match your content
              {Object.keys(form.cats).length > 0 && (
                <>: <span className="font-semibold text-gray-900">
                  {Object.keys(form.cats).map(cat_id2lbl).join(', ')}</span></>
              )}
            </p>
          </div>
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
              {cat_ids.map(id => (
                <button
                  key={id}
                  onClick={() => {
                    let cats = {...form.cats};
                    if (cats[id])
                      delete cats[id];
                    else
                      cats[id] = 1;
                    form_set({...form, cats});
                  }}
                  className={`px-3 sm:px-4 py-2 sm:py-3 rounded-lg border-2 font-medium text-xs sm:text-sm transition-all ${
                    form.cats[id]
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {cat_id2lbl(id)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {Object.keys(org_socials).length > 0 && (
          <div className="bg-white rounded-xl border shadow-sm mt-4 sm:mt-6">
            <div className="p-4 sm:p-6 border-b flex items-center justify-between">
              <div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900">Social Stats</h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">Numbers stored on your org</p>
              </div>
              {org_tier && (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full
                  text-xs font-semibold uppercase">
                  Tier: {org_tier}
                </span>
              )}
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              {Object.entries(org_socials).map(([_platform, s]) => (
                <div key={_platform} className="border rounded-lg p-3 sm:p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-gray-900 text-sm">
                      {platform.platform_lbl(_platform)}
                    </h4>
                    {s.is_connected && (
                      <span className="text-xs text-green-600 font-medium">Connected</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                    {([['followers', 'Followers'], ['avg_views', 'Avg views'],
                      ['med_views', 'Med views'], ['avg_likes', 'Avg likes'],
                      ['avg_cmts', 'Avg comments'], ['avg_shares', 'Avg shares'],
                      ['avg_saves', 'Avg saves']] as const).map(([k, lbl]) =>
                      s[k] != undefined && (
                        <div key={k} className="bg-gray-50 px-2 py-1 rounded">
                          {lbl}: <b>{s[k].toLocaleString()}</b>
                        </div>
                      ))}
                    {s.tier && (
                      <div className="bg-gray-50 px-2 py-1 rounded">Tier: <b>{s.tier}</b></div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {is_dbg && (
        <div className="bg-white rounded-xl border shadow-sm mt-4 sm:mt-6">
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-semibold text-gray-700">Brand Account</label>
                <p className="text-xs text-gray-500 mt-1">
                  Enable to access campaign creation and management
                </p>
              </div>
              <button
                onClick={brand_flag_toggle}
                disabled={flag_loading}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                  is_brand ? 'bg-green-500' : 'bg-gray-300'
                } ${flag_loading ? 'opacity-50' : ''}`}
              >
                <span className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                  is_brand ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            {is_brand ? (
              <p className="text-xs text-green-600 mt-2 font-medium">Brand flag is active</p>
            ) : (
              <p className="text-xs text-red-500 mt-2 font-medium">
                Brand flag is not set - campaigns will not load
              </p>
            )}
          </div>
        </div>
        )}

        <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
          <button
            onClick={save}
            disabled={saving}
            className="w-full sm:flex-1 px-4 sm:px-6 py-3 bg-gradient-to-r from-[#0066FF] to-[#6B4DE6] text-white rounded-lg font-semibold text-sm sm:text-base hover:shadow-lg transition-all disabled:opacity-50"
          >
            {saving ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent
                  rounded-full animate-spin"/>
                Saving...
              </span>
            ) : 'Save Changes'}
          </button>
          <button
            onClick={() => navigate('/creator/dashboard')}
            className="w-full sm:w-auto px-4 sm:px-6 py-3 border border-gray-300 rounded-lg font-semibold text-sm sm:text-base text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>

    {logo_modal_open && (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center
        justify-center z-50 p-4"
        onClick={() => { if (!logo_busy) logo_modal_open_set(false); }}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8"
          onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">Change Logo</h3>
            <button type="button" onClick={() => logo_modal_open_set(false)}
              disabled={logo_busy}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none disabled:opacity-50"
            >×</button>
          </div>
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Upload from your device
              </label>
              <input type="file" accept="image/*" onChange={logo_file_chosen}
                className="hidden" id="creator-logo-upload-modal" disabled={logo_busy} />
              <label
                htmlFor="creator-logo-upload-modal"
                className={`block w-full text-center px-4 py-3 border-2 border-dashed
                  border-gray-300 rounded-lg text-sm font-semibold text-gray-700
                  hover:bg-gray-50 cursor-pointer transition-all ${
                    logo_busy ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {uploading_logo ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent
                      rounded-full animate-spin"/>
                    Uploading...
                  </span>
                ) : 'Choose file'}
              </label>
              <p className="text-xs text-gray-500 mt-2">PNG or JPG • Max 2MB</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-px bg-gray-200"/>
              <span className="text-xs text-gray-400 uppercase tracking-wide">or</span>
              <div className="flex-1 h-px bg-gray-200"/>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Provide a URL</label>
              <input type="url" value={logo_url_input}
                onChange={e => { logo_url_input_set(e.target.value); logo_url_err_set(''); }}
                placeholder="https://example.com/logo.png" disabled={logo_busy}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2
                  focus:ring-blue-500 focus:border-transparent ${
                    logo_url_err ? 'border-red-500' : 'border-gray-300'}`} />
              {logo_url_err && <p className="text-red-500 text-xs mt-1">{logo_url_err}</p>}
              <button
                type="button"
                onClick={logo_url_use}
                disabled={logo_busy || !logo_url_input.trim()}
                className="mt-3 w-full px-4 py-2 bg-gradient-brand text-white rounded-lg
                  text-sm font-semibold hover:shadow-lg transition-all disabled:opacity-50
                  disabled:cursor-not-allowed"
              >
                {saving_logo_url ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent
                      rounded-full animate-spin"/>
                    Fetching & uploading...
                  </span>
                ) : 'Use URL'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {alert_msg && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        onClick={() => alert_msg_set('')}>
        <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6"
          onClick={e => e.stopPropagation()}>
          <p className="text-gray-900 text-sm sm:text-base mb-5">{alert_msg}</p>
          <div className="flex justify-end">
            <button onClick={() => alert_msg_set('')}
              className="px-4 py-2 bg-gradient-brand text-white rounded-lg font-semibold text-sm hover:opacity-90">
              OK
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}